import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, map, Observable, of, tap, throwError } from 'rxjs';
import { ErrorKeys } from '../../components/error-messages/error-messages.component';
import { AuthService } from '../../core/services/auth/auth.service';
import { CachedContent } from '../../interfaces/cached-content';
import { EkycSession } from '../../interfaces/ekyc';
import { ApiConfigService } from '../../ssr-services/api-config/api-config.service';
import { CacheService } from '../../ssr-services/cache/cache.service';

/**
 * Configuration options for initiateEkycFlow
 */
export interface InitiateEkycFlowOptions {
  /** Optional checkout plan ID */
  checkoutPlanId?: string;
  /** Optional function to transform status before updating eKyc details */
  statusTransform?: (status: string) => string;
  /** Whether to clear server errors on success (default: true) */
  clearErrorsOnSuccess?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class IdentityVerificationService {
  private readonly apiConfigService = inject(ApiConfigService);

  private readonly authService = inject(AuthService);

  public cacheService = inject(CacheService);

  public http = inject(HttpClient);

  readonly eKycDetails = signal<EkycSession | null>(null);

  /** Loading state for eKyc initiation flow */
  readonly isSubmitting = signal(false);

  /** Server validation errors from eKyc initiation */
  readonly serverErrors = signal({
    [ErrorKeys.ServerValidation]: '',
  });

  public config = computed(() => (this.cacheService.cachedContent() as CachedContent).cache.config);

  /**
   * Computed signal that determines if eKyc functionality is enabled globally
   * @param eKycRequired - Whether eKyc is required for the specific destination/plan
   * @returns boolean - true if both eKyc is enabled in config AND required for the destination
   */
  public isEKycEnabled = (eKycRequired: boolean) =>
    computed(() => {
      const config = this.config();
      return config?.eKycEnabled && eKycRequired;
    });

  constructor() {
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();
      if (!isAuthenticated) {
        this.eKycDetails.set(null);
      }
    });
  }

  updateEkycDetails(details: EkycSession) {
    this.eKycDetails.update((current) => ({
      ...current,
      ...details,
    }));
  }

  public initiateEkyc(data: { checkoutPlanId: string }): Observable<EkycSession> {
    return this.http.post<EkycSession>(this.apiConfigService.url('ekyc'), data);
  }

  public sendEkycLink(data: {
    email: string;
    eKycSessionId: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(this.apiConfigService.url('ekyc/email'), data);
  }

  /**
   * Initiates eKyc flow with built-in error handling, loading state management,
   * and automatic eKyc details update.
   *
   * This method centralizes the eKyc initiation logic to avoid code duplication
   * across components.
   *
   * @param options - Configuration options for the flow
   * @returns Observable<boolean> - true on success, false if already submitting
   */
  initiateEkycFlow(options: InitiateEkycFlowOptions = {}): Observable<boolean> {
    const { checkoutPlanId = '', statusTransform, clearErrorsOnSuccess = true } = options;

    if (this.isSubmitting()) {
      return of(false);
    }

    this.isSubmitting.set(true);

    return this.initiateEkyc({ checkoutPlanId }).pipe(
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 400) {
          this.serverErrors.set({
            [ErrorKeys.ServerValidation]: err.error.message,
          });
        }
        return throwError(() => err);
      }),
      tap({
        next: (res) => {
          // Clear any previous server errors on success
          if (clearErrorsOnSuccess) {
            this.serverErrors.set({
              [ErrorKeys.ServerValidation]: '',
            });
          }

          const status = statusTransform ? statusTransform(res.status) : res.status;

          this.updateEkycDetails({
            status,
            sessionId: res.sessionId,
            sessionToken: res.sessionToken,
            sessionUrl: res.sessionUrl,
          });
        },
      }),
      map(() => true),
      finalize(() => this.isSubmitting.set(false))
    );
  }
}
